import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ga4Summary, analyticsStatus, analyticsConnect, analyticsDisconnect, analyticsTest, fmtMoney, fmtNum, fmtPct } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ChartLineUp, Users, Eye, Target, CurrencyDollar, Plug, CheckCircle, Globe } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRESETS = [{ l: "7d", d: 7 }, { l: "28d", d: 28 }, { l: "90d", d: 90 }];
const rangeFor = (days) => {
  const e = new Date(), s = new Date(); s.setDate(e.getDate() - (days - 1));
  const f = (d) => d.toISOString().slice(0, 10);
  return { start: f(s), end: f(e) };
};

function ConnectGA4({ connected, onConnect, onDisconnect, onTest, testResult, testing }) {
  const [open, setOpen] = useState(false);
  const [prop, setProp] = useState("");
  const [creds, setCreds] = useState("");
  return (
    <>
      {connected ? (
        <div className="flex items-center gap-2">
          <span data-testid="ga4-connected-badge" className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 flex items-center gap-1"><CheckCircle size={13} weight="fill" /> Live</span>
          <Button data-testid="ga4-test-btn" size="sm" variant="outline" onClick={onTest} disabled={testing} className="border-white/15 bg-transparent text-white">{testing ? "Testing…" : "Test Connection"}</Button>
          <Button data-testid="ga4-disconnect-btn" size="sm" variant="outline" onClick={onDisconnect} className="border-white/15 bg-transparent text-white">Disconnect</Button>
        </div>
      ) : (
        <Button data-testid="ga4-connect-btn" size="sm" onClick={() => setOpen(true)} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><Plug size={15} className="mr-1.5" /> Connect GA4</Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-head">Connect Google Analytics 4</DialogTitle>
            <DialogDescription className="text-[#7a7a7a]">Read-only. Enter your numeric GA4 Property ID (not the G- Measurement ID) and a service-account JSON with Viewer access. Credentials are verified against the live GA4 API before the connection is marked active, stored encrypted server-side, and never sent back to the browser.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-caps">GA4 Property ID (numeric)</Label><Input data-testid="ga4-property-input" value={prop} onChange={(e) => setProp(e.target.value)} placeholder="e.g. 545466367" className="bg-[#10101A] border-white/10 mt-1" /></div>
            <div><Label className="label-caps">Service Account Credentials (JSON)</Label>
              <textarea data-testid="ga4-creds-input" value={creds} onChange={(e) => setCreds(e.target.value)} rows={6}
                placeholder='{"type":"service_account","project_id":"...","private_key":"...","client_email":"...@....iam.gserviceaccount.com",...}'
                className="w-full bg-[#10101A] border border-white/10 rounded-md mt-1 p-2 text-xs font-mono text-white placeholder:text-[#5a5a5a]" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="ga4-save-btn" onClick={() => { onConnect({ GA4_PROPERTY_ID: prop, GA4_CREDENTIALS_JSON: creds }); setOpen(false); }} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Verify & connect</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function GA4Page() {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const [days, setDays] = useState(28);
  const { start, end } = rangeFor(days);

  const statusQ = useQuery({ queryKey: ["analytics-status", activeId], queryFn: () => analyticsStatus(activeId), enabled: !!activeId });
  const connected = statusQ.data?.ga4?.connected;
  const q = useQuery({ queryKey: ["ga4", activeId, days], queryFn: () => ga4Summary(activeId, start, end), enabled: !!activeId });
  const d = q.data;

  const connectM = useMutation({ mutationFn: (creds) => analyticsConnect(activeId, "ga4", creds),
    onSuccess: (r) => {
      if (r.verified) toast.success(r.message || "GA4 connected — live data verified");
      else toast.error(r.message || "GA4 verification failed — credentials were not saved");
      qc.invalidateQueries({ queryKey: ["analytics-status", activeId] }); qc.invalidateQueries({ queryKey: ["ga4", activeId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not connect GA4") });
  const disconnectM = useMutation({ mutationFn: () => analyticsDisconnect(activeId, "ga4"),
    onSuccess: () => { toast.success("GA4 disconnected"); qc.invalidateQueries({ queryKey: ["analytics-status", activeId] }); qc.invalidateQueries({ queryKey: ["ga4", activeId] }); } });
  const testM = useMutation({ mutationFn: () => analyticsTest(activeId, "ga4"),
    onSuccess: (r) => { r?.ok ? toast.success(r.message || "GA4 connection verified") : toast.error(r?.message || "GA4 connection test failed"); } });

  const t = d?.totals || {};
  return (
    <PageContainer>
      <PageHeader eyebrow="Analytics" title="Google Analytics 4"
        subtitle="Read-only traffic, engagement, conversion and attribution. Connect Google Analytics to view live traffic data — no numbers are shown until a real property is connected.">
        <ConnectGA4 connected={connected} onConnect={connectM.mutate} onDisconnect={disconnectM.mutate}
          onTest={() => testM.mutate()} testing={testM.isPending} />
      </PageHeader>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <span data-testid="ga4-data-source" className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${d?.data_source === "live" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25" : "bg-white/5 text-[#9a9a9a] border-white/10"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${d?.data_source === "live" ? "bg-emerald-400" : "bg-[#7a7a7a]"}`} />
          {d?.data_source === "live" ? "Live GA4 data" : connected ? "Connected — no data received yet (new properties can take 24-48h)" : "No analytics connected — connect GA4 to see live data"}
        </span>
        <div className="flex rounded-lg bg-white/5 p-1">
          {PRESETS.map((p) => (
            <button key={p.l} data-testid={`ga4-range-${p.l}`} onClick={() => setDays(p.d)} className={`px-3 py-1.5 rounded-md text-sm ${days === p.d ? "bg-[#6B3DFF] text-white" : "text-[#9a9a9a]"}`}>{p.l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <StatCard label="Visitors (Users)" value={fmtNum(t.users)} delta={d?.deltas?.users} icon={Users} accent />
        <StatCard label="Sessions" value={fmtNum(t.sessions)} delta={d?.deltas?.sessions} icon={ChartLineUp} />
        <StatCard label="Page Views" value={fmtNum(t.pageviews)} icon={Eye} />
        <StatCard label="Conversion Rate" value={fmtPct(t.conversion_rate)} icon={Target} accent />
        <StatCard label="Conversions" value={fmtNum(t.conversions)} delta={d?.deltas?.conversions} icon={CheckCircle} />
        <StatCard label="Revenue" value={fmtMoney(t.revenue)} delta={d?.deltas?.revenue} icon={CurrencyDollar} accent />
      </div>

      <div className="card-matte p-5 mb-6" data-testid="ga4-sessions-chart">
        <div className="label-caps mb-3">Sessions over time</div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={d?.series || []}>
            <defs><linearGradient id="ga" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6B3DFF" stopOpacity={0.4} /><stop offset="100%" stopColor="#6B3DFF" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
            <XAxis dataKey="date" tick={{ fill: "#7a7a7a", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fill: "#7a7a7a", fontSize: 11 }} />
            <Tooltip contentStyle={{ background: "#14141F", border: "1px solid #ffffff20", borderRadius: 8 }} />
            <Area type="monotone" dataKey="sessions" stroke="#6B3DFF" strokeWidth={2.5} fill="url(#ga)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <div className="card-matte p-5" data-testid="ga4-traffic-sources">
          <div className="label-caps mb-3 flex items-center gap-1"><Globe size={13} className="brand-text" /> Traffic Sources</div>
          <div className="space-y-2.5">
            {(d?.traffic_sources || []).map((s) => (
              <div key={s.source}>
                <div className="flex items-center justify-between text-sm"><span className="text-[#cfcfcf]">{s.source}</span><span className="text-[#9a9a9a]">{fmtNum(s.sessions)} · {s.pct}%</span></div>
                <div className="h-1.5 bg-white/8 rounded-full mt-1 overflow-hidden"><div className="h-full bg-[#6B3DFF]" style={{ width: `${s.pct}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
        <div className="card-matte p-5" data-testid="ga4-landing-pages">
          <div className="label-caps mb-3">Top Landing Pages</div>
          <Table>
            <TableHeader><TableRow className="border-white/10 hover:bg-transparent">{["Page", "Sessions", "CVR", "Bounce"].map((h) => <TableHead key={h} className="label-caps text-[#7a7a7a]">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{(d?.top_landing_pages || []).map((p, i) => (
              <TableRow key={i} className="border-white/8 hover:bg-white/[0.03]"><TableCell className="text-[#cfcfcf] truncate max-w-[160px]">{p.path}</TableCell><TableCell>{fmtNum(p.sessions)}</TableCell><TableCell>{fmtPct(p.conversion_rate)}</TableCell><TableCell>{fmtPct(p.bounce_rate)}</TableCell></TableRow>
            ))}</TableBody>
          </Table>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <div className="card-matte p-5" data-testid="ga4-product-performance">
          <div className="label-caps mb-3">Product Performance</div>
          <Table>
            <TableHeader><TableRow className="border-white/10 hover:bg-transparent">{["Product", "Views", "ATC", "Purchases", "Revenue"].map((h) => <TableHead key={h} className="label-caps text-[#7a7a7a]">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{(d?.product_performance || []).map((p, i) => (
              <TableRow key={i} className="border-white/8 hover:bg-white/[0.03]"><TableCell className="text-[#cfcfcf]">{p.name}</TableCell><TableCell>{fmtNum(p.item_views)}</TableCell><TableCell>{fmtNum(p.add_to_cart)}</TableCell><TableCell>{fmtNum(p.purchases)}</TableCell><TableCell className="brand-text">{fmtMoney(p.revenue)}</TableCell></TableRow>
            ))}</TableBody>
          </Table>
        </div>
        <div className="card-matte p-5" data-testid="ga4-attribution">
          <div className="label-caps mb-3">Revenue Attribution</div>
          <div className="space-y-2 mb-5">
            {(d?.revenue_attribution || []).slice(0, 6).map((a) => (
              <div key={a.channel} className="flex items-center justify-between text-sm"><span className="text-[#cfcfcf]">{a.channel}</span><span className="brand-text font-semibold">{fmtMoney(a.revenue)} · {a.pct}%</span></div>
            ))}
          </div>
          <div className="label-caps mb-3">Campaign Attribution</div>
          <div className="space-y-2">
            {(d?.campaign_attribution || []).map((c) => (
              <div key={c.campaign} className="flex items-center justify-between text-sm"><span className="text-[#cfcfcf] truncate max-w-[180px]">{c.campaign}</span><span className="text-[#9a9a9a]">{c.conversions} conv · {fmtMoney(c.revenue)}</span></div>
            ))}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
